import { ChevronLeft, ChevronRight, AlertCircle, Users, Briefcase, Star } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useVehicles } from '../../hooks/useVehicles';


const VehicleSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [autoplay, setAutoplay] = useState(true);

  const updateCardsPerPage = useCallback(() => {
    if (window.innerWidth < 768) {
      setCardsPerPage(1);
    } else if (window.innerWidth < 1024) {
      setCardsPerPage(2);
    } else {
      setCardsPerPage(3);
    }
  }, []);

  useEffect(() => {
    updateCardsPerPage();
    window.addEventListener('resize', updateCardsPerPage);
    return () => window.removeEventListener('resize', updateCardsPerPage);
  }, [updateCardsPerPage]);

  const { data: vehicles, isLoading, error } = useVehicles();

  useEffect(() => {
    if (!vehicles?.length || !autoplay) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex >= Math.max(0, vehicles.length - cardsPerPage) ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [vehicles, cardsPerPage, autoplay]);

  const nextSlide = () => {
    if (vehicles) {
      setAutoplay(false);
      setCurrentIndex((prevIndex) => 
        prevIndex >= Math.max(0, vehicles.length - cardsPerPage) ? 0 : prevIndex + 1
      );
    }
  };

  const prevSlide = () => {
    if (vehicles) {
      setAutoplay(false);
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? Math.max(0, vehicles.length - cardsPerPage) : prevIndex - 1
      );
    }
  };

  if (error) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-md mx-auto">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Unable to load fleet</h3>
            <p className="text-red-600">Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white" aria-labelledby="fleet-heading">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-primary uppercase tracking-widest text-sm font-medium">Our Fleet</span>
          <h2 id="fleet-heading" className="text-3xl md:text-4xl font-serif font-bold text-secondary mt-3 mb-4">
            Luxury Vehicles for Every Occasion
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg">
            Choose from our meticulously maintained fleet of premium vehicles
          </p>
        </div>
        
        {isLoading ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : vehicles && vehicles.length > 0 ? (
          <div className="relative">
            <div className="overflow-hidden mx-8">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * (100 / cardsPerPage)}%)` }}
              >
                {vehicles.map((vehicle) => (
                  <div 
                    key={vehicle.id} 
                    className="px-3"
                    style={{ width: `${100 / cardsPerPage}%`, flexShrink: 0 }}
                  >
                    <div className="bg-neutral-cream rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full group">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={vehicle.image_url || '/chauffeur-prive-dans-le-monde-scaled-1-e1723452158165.jpg'}
                          alt={vehicle.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                          €{vehicle.price_per_km}/km
                        </div>
                        {vehicle.type === 'luxury' && (
                          <div className="absolute top-4 left-4 bg-secondary text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                            <Star className="w-3 h-3 fill-primary text-primary" /> Premium
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-serif font-bold text-secondary mb-2">
                          {vehicle.name}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm">{vehicle.description}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-4 text-gray-600">
                            <div className="flex items-center gap-1" title="Passenger capacity">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{vehicle.capacity}</span>
                            </div>
                            <div className="flex items-center gap-1" title="Luggage capacity">
                              <Briefcase className="w-4 h-4" />
                              <span className="text-sm">{vehicle.luggage_capacity}</span>
                            </div>
                          </div>
                          <Link
                            to="/reservation"
                            className="text-primary font-medium text-sm hover:text-primary-dark transition-colors"
                          >
                            Book Now →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white hover:bg-neutral-cream p-3 rounded-full shadow-lg transition-all z-10 border border-gray-100"
              aria-label="Previous vehicle"
            >
              <ChevronLeft className="w-6 h-6 text-secondary" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white hover:bg-neutral-cream p-3 rounded-full shadow-lg transition-all z-10 border border-gray-100"
              aria-label="Next vehicle"
            >
              <ChevronRight className="w-6 h-6 text-secondary" />
            </button>

            <div className="flex justify-center mt-8 gap-2">
              {Array.from({ length: Math.ceil(vehicles.length / cardsPerPage) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index * cardsPerPage);
                    setAutoplay(false);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    Math.floor(currentIndex / cardsPerPage) === index 
                      ? 'bg-primary w-8' 
                      : 'bg-gray-300 w-2 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 bg-neutral-cream rounded-xl">
            <p className="text-gray-600 mb-4">Our fleet is being prepared. Check back soon!</p>
            <Link 
              to="/fleet" 
              className="inline-block px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              View Fleet Page
            </Link>
          </div>
        )}
        
        <div className="text-center mt-12">
          <Link 
            to="/fleet" 
            className="inline-flex items-center px-8 py-4 bg-secondary text-white font-medium rounded-lg hover:bg-secondary-dark transition-colors"
          >
            Explore Our Complete Fleet
          </Link>
        </div>
      </div>
    </section>
  );
};

export default VehicleSection;
